import { Drawer, Field, InputNumber, RadioGroup, Scrollbar, Select } from '@repo/go-ui';
import clsx from 'clsx';
import { observer } from 'mobx-react-lite';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import type { HeatmapWidgetSettingsStore } from '../../store/SettingsStore/HeatmapWidgetSettingsStore';
import {
  ANALYSIS_AXIS_MAP,
  STEP_RANGE_AXIS_MAP,
  VALUE_RANGE_AXIS_MAP,
} from '../../store/SettingsStore/settingsStoreConfig';
import type { DrillingMode } from '../../store/types';
import { StepRangeKey, ValueRangeKey } from '../../store/types';

import styles from './settings-drawer.module.scss';
import {
  ANALYSIS_TYPE_OPTIONS,
  ANALYSIS_TYPE_PARAMETERS,
  DRILLING_OPTIONS,
  INTERVAL_OPTIONS,
  RANGE_PARAMETERS,
  STEP_PARAMETERS,
  Z_FILTER_ROTARY_PARAMETERS,
  Z_FILTER_SLIDE_PARAMETERS,
} from './SettingsOptions';

const RegimeSettingsSection = observer(
  ({ settings, regime }: { settings: HeatmapWidgetSettingsStore; regime: 'rotation' | 'slide' }) => {
    const { t } = useTranslation('heatmapWidget');
    const rotation = regime === 'rotation';
    const filters = rotation ? Z_FILTER_ROTARY_PARAMETERS : Z_FILTER_SLIDE_PARAMETERS;
    const options = DRILLING_OPTIONS.map((option) => ({
      ...option,
      label:
        settings.getRangeDisplayName(option.value === 'load' ? ValueRangeKey.WOB : ValueRangeKey.DPRESS) ??
        t(option.label),
      style: { gap: 16, marginTop: 0, marginRight: 16 },
    }));
    return (
      <div className={clsx(styles.block, styles.bordered)}>
        <div className={styles.title}>{t(rotation ? 'Heatmap-rotary-drilling' : 'Heatmap-slide-drilling')}</div>
        <RadioGroup
          style={{ display: 'flex', gap: 8, alignItems: 'center' }}
          items={options}
          value={rotation ? settings.rotorDrilling : settings.slideDrilling}
          onChange={(value: DrillingMode) =>
            rotation ? settings.setRotorDrilling(value) : settings.setSlideDrilling(value)
          }
        />
        <div className={styles.grid}>
          <span className={clsx(styles.header, styles.spanAll)}>{t('Heatmap-filter-title')}</span>
          <span />
          <span className={styles.header}>{t('Min')}</span>
          <span className={styles.header}>{t('Max')}</span>
          {filters.map(({ key, label: labelKey }) => {
            const label = settings.getZFilterDisplayName(key) ?? t(labelKey);
            return (
              <Fragment key={key}>
                <span className={styles.label}>{label}</span>
                {(['min', 'max'] as const).map((bound) => (
                  <label key={bound}>
                    <span>{`${t(rotation ? 'Heatmap-rotary-drilling' : 'Heatmap-slide-drilling')} ${label} ${t(bound === 'min' ? 'Min' : 'Max')}`}</span>
                    <InputNumber
                      size="s"
                      min={0}
                      value={settings.zFilterParams[key][bound]}
                      onChange={(value) =>
                        settings.setZFilter(key, {
                          [bound]: value === null || value === '' ? null : Number(value),
                        })
                      }
                    />
                  </label>
                ))}
              </Fragment>
            );
          })}
        </div>
      </div>
    );
  },
);

export const SettingsDrawer = observer(
  ({
    settings,
    isSettingsDrawerOpen,
    setSettingsDrawerOpen,
    externalContainer,
    theme,
  }: {
    settings: HeatmapWidgetSettingsStore;
    isSettingsDrawerOpen: boolean;
    setSettingsDrawerOpen: (value: boolean) => void;
    externalContainer?: Element;
    theme: 'light' | 'dark';
  }) => {
    const { t } = useTranslation('heatmapWidget');
    const analysisOptions = ANALYSIS_TYPE_OPTIONS.map((option) => ({
      ...option,
      label: t(option.label),
    }));
    return (
      <Drawer
        title={t('Heatmap-chart-settings')}
        open={isSettingsDrawerOpen}
        onClose={() => setSettingsDrawerOpen(false)}
        style={{ width: '480px', maxWidth: '100%' }}
        container={externalContainer}
      >
        <Scrollbar>
          <div className={`go-widget-theme-${theme}`} style={{ padding: '8px 16px' }}>
            <div className={clsx(styles.block, styles.bordered)}>
              <Field label={t('Heatmap-interval-analysis')}>
                <Select
                  options={INTERVAL_OPTIONS.map((option) => ({
                    ...option,
                    label: t(option.label),
                  }))}
                  value={settings.interval}
                  onSelect={(value) => settings.setInterval(value)}
                />
              </Field>
            </div>
            <RegimeSettingsSection settings={settings} regime="rotation" />
            <RegimeSettingsSection settings={settings} regime="slide" />
            <div className={clsx(styles.block, styles.bordered)}>
              <div className={styles.title}>{t('Heatmap-value-range-title')}</div>
              <div className={styles.grid}>
                <span />
                <span className={styles.header}>{t('Min')}</span>
                <span className={styles.header}>{t('Max')}</span>
                {RANGE_PARAMETERS.map(({ key, label: labelKey }) => {
                  const axisKey = VALUE_RANGE_AXIS_MAP[key];
                  const label = settings.getRangeDisplayName(key) ?? t(labelKey);
                  return (
                    <Fragment key={key}>
                      <span className={styles.label}>{label}</span>
                      {(['min', 'max'] as const).map((bound) => (
                        <label key={bound}>
                          <span>{`${label} ${t(bound === 'min' ? 'Min' : 'Max')}`}</span>
                          <InputNumber
                            size="s"
                            min={0}
                            value={settings.axisParams[axisKey][bound]}
                            onChange={(value) =>
                              settings.updateAxis(axisKey, {
                                [bound]: Number(value),
                              })
                            }
                          />
                        </label>
                      ))}
                    </Fragment>
                  );
                })}
              </div>
            </div>
            <div className={clsx(styles.block, styles.bordered)}>
              <div className={styles.title}>{t('Heatmap-analysis-step')}</div>
              <div className={styles.grid}>
                {STEP_PARAMETERS.map((option) => {
                  const isRpmStep = option.key.includes(StepRangeKey.RPM);
                  const axisKey = isRpmStep ? settings.currentXAxisKey : STEP_RANGE_AXIS_MAP[option.key[0]];
                  const axis = settings.axisParams[axisKey];
                  const label =
                    settings.getStepDisplayName(option.key) ??
                    t(isRpmStep ? settings.currentXAxisLabelKey : option.label);
                  return (
                    <Fragment key={axisKey}>
                      <span className={styles.label}>{label}</span>
                      <label className={option.unitKey ? undefined : styles.spanTwo}>
                        <span>{`${label} ${t('Heatmap-analysis-step')}`}</span>
                        <InputNumber
                          size="s"
                          value={axis.step}
                          onChange={(value) =>
                            settings.updateAxis(axisKey, {
                              step: Number(value),
                            })
                          }
                        />
                      </label>
                      {option.unitKey && (
                        <Select
                          options={settings.unitOptions(option.unitKey)}
                          value={axis.unit.id}
                          onSelect={(id, { label: abbreviation }) =>
                            settings.updateAxis(axisKey, {
                              unit: { id, abbreviation },
                            })
                          }
                        />
                      )}
                    </Fragment>
                  );
                })}
              </div>
            </div>
            <div className={clsx(styles.block, styles.bordered)}>
              <div className={styles.title}>{t('Heatmap-analysis-method')}</div>
              <div className={styles.grid}>
                {ANALYSIS_TYPE_PARAMETERS.map(({ key, label, unitKey }) => {
                  const axisKey = ANALYSIS_AXIS_MAP[key];
                  const axis = settings.axisParams[axisKey];
                  return (
                    <Fragment key={key}>
                      <span className={styles.label}>{settings.getAnalysisDisplayName(key) ?? t(label)}</span>
                      <Select
                        options={analysisOptions}
                        value={axis.analysisType}
                        onSelect={(analysisType) => settings.updateAxis(axisKey, { analysisType })}
                      />
                      <Select
                        options={settings.unitOptions(unitKey)}
                        value={axis.unit.id}
                        onSelect={(id, { label: abbreviation }) =>
                          settings.updateAxis(axisKey, {
                            unit: { id, abbreviation },
                          })
                        }
                      />
                    </Fragment>
                  );
                })}
              </div>
            </div>
            <div className={styles.block}>
              <div className={styles.grid}>
                <span className={styles.label}>{t('Heatmap-last-bins-range')}</span>
                <label className={styles.spanTwo}>
                  <span>{t('Heatmap-last-bins-range')}</span>
                  <InputNumber
                    size="s"
                    value={settings.lastBinsRange}
                    min={1}
                    max={settings.lastBinsRangeMax}
                    step={1}
                    suffix={settings.depthUnit}
                    onChange={(value) => {
                      settings.setLastBinsRange(Number(value));
                    }}
                  />
                </label>
              </div>
            </div>
          </div>
        </Scrollbar>
      </Drawer>
    );
  },
);
